import { FC } from 'react';

import { CfgQuestionQuantity, CfgQuestionValue } from '../app/types/catalog-types'
import { Checkbox } from '../controls/checkbox/checkbox'
import { useEnvConfig } from '../app/context/environment-context'
import { ThumbnailMenu, ThumbnailMenuItem } from '../controls/thumbnail-menu'
import InfoSubtitle from '../controls/infoSubtitle/info-subtitle'

import minusIcon from '../img/minus-solid.svg'
import plusIcon from '../img/plus-solid.svg'
import styles from './configurator-q-quantity.module.css'
import { useMessages2 } from '../hooks/useLanguage';

const DEFAULT_MAX_QUANTITY = 99;

export function ConfiguratorQuantityQuestion({ question, selectedValue, quantity, values, 
                                               maxShowableQuantity, onValuesChange } : {
    question: CfgQuestionQuantity    
    selectedValue: string | null
    quantity: number
    maxShowableQuantity?: number
    values: CfgQuestionValue[]
    onValuesChange: (values: string[], quantity: number) => void
}) {

    const onUndoSelect = () => {
        onValuesChange([], 0)
    }

    const props = {
        handleValueChange: onValuesChange,
        onUndoSelect: onUndoSelect,
        question, selectedValue, quantity, values, maxShowableQuantity,
    }

    if(question.uitype === 'checkbox'){
        return <ConfiguratorQuantityCheckbox {...props} />
    } else if(question.uitype === 'radio'){
        return <ConfiguratorQuantityRadio {...props} />
    } else if(question.uitype === 'thumb'){
        return <ConfiguratorQuantityEnumThumb {...props} />
    }
    
}

export type ConfiguratorQuantityProps = {
    question: CfgQuestionQuantity,
    selectedValue: string | null
    values: CfgQuestionValue[]
    quantity: number
    maxShowableQuantity?: number
    handleValueChange: (values: string[], qt: number) => void
    onUndoSelect: () => void
}

export function ConfiguratorQuantityRadio({ quantity, question, selectedValue, values, maxShowableQuantity,
                                            handleValueChange, onUndoSelect }: ConfiguratorQuantityProps) {
    return <form className={styles.enumYesNoCtrl}>
            
    {values.map((value) => {
        const id = question.id + '-' + value.values[0];

        return (
            <>
                <div className={styles.eyncCtrl}>
                    <input 
                        disabled={value.disabled ?? false} 
                        type="radio" 
                        id={id} 
                        name={question.id} 
                        checked={value.values[0] === selectedValue}
                        onChange={() => handleValueChange(value.values, 1)}
                    />
                
                { value.title ? <label className={`${styles.eyncTitle} ${value.disabled ? styles.disabled : ''}`} htmlFor={id}>
                                    {value.title}
                                </label>
                                :
                                null
                }

                {selectedValue === value.values[0] && 
                    <QuantityQuestionItem 
                        quantity={quantity}
                        maxQuantity={value?.maxQuantity ?? DEFAULT_MAX_QUANTITY}
                        maxShowableQuantity={maxShowableQuantity}
                        value={value} 
                        showThumbnail={true}
                        onUndoSelect={onUndoSelect}
                        onQuantityChange={(quantity) => handleValueChange(value.values, quantity)}
                    />
                }
                </div>
            </>
        )
    })}
</form>

}

export function ConfiguratorQuantityRadioThumb({ question, quantity, selectedValue, values, maxShowableQuantity,
                                                 handleValueChange, onUndoSelect }: ConfiguratorQuantityProps) {
    return <form className={styles.enumYesNoCtrl}>
            
    {values.map((value) => {
        const id = question.id + '-' + value.values[0];

        return (
            <>
                <div className={styles.eyncCtrl}>
                    <input 
                        disabled={value.disabled ?? false} 
                        type="radio" 
                        id={id} 
                        name={question.id} 
                        checked={value.values[0] === selectedValue}
                        onChange={() => handleValueChange(value.values, 1)}
                    />
                
                { value.title ? <label className={`${styles.eyncTitle} ${value.disabled ? styles.disabled : ''}`} htmlFor={id}>
                                    {value.title}
                                </label>
                                :
                                null
                }

                {selectedValue === value.values[0] && 
                    <QuantityQuestionItem 
                        quantity={quantity}
                        maxQuantity={value?.maxQuantity ?? DEFAULT_MAX_QUANTITY} 
                        maxShowableQuantity={maxShowableQuantity}
                        value={value} 
                        showThumbnail={true}
                        onUndoSelect={onUndoSelect}
                        onQuantityChange={(quantity) => handleValueChange(value.values, quantity)}
                    />
                }
                </div>
            </>
        )
    })}
</form>

}

export function QuantityQuestionItem({ quantity, value, maxQuantity, maxShowableQuantity,
                                       onUndoSelect, onQuantityChange, showThumbnail } : {
    quantity: number
    value: CfgQuestionValue
    maxQuantity: number
    maxShowableQuantity?: number
    showThumbnail: boolean
    onUndoSelect: () => void
    onQuantityChange: (qt: number) => void
}) {

    const { thumbnailsPath, thumbnailWidth, thumbnailHeight } = useEnvConfig();
    const { messages } = useMessages2();

    const changeQuantity = (value: number) => {
        const newValue = quantity + value;

        if(newValue > 0 && newValue <= maxQuantity) {
            onQuantityChange(newValue)
        }

        if(newValue === 0){
            onUndoSelect()
        }
    }

    return (
        <div className={styles.numberContainer}>
                                
        {value.thumbnail && showThumbnail && <img src={`${thumbnailsPath}/${value.thumbnail ?? ''}`} className={styles.img}
        width={thumbnailWidth} height={thumbnailHeight}/>}

        {maxQuantity > 1 && 
            (<div className={styles.quantityQuestionItem}>
                <button className={styles.roundButton} type='button' onClick={() => changeQuantity(-1)}>
                    <img src={minusIcon}/>
                </button>
                <span className={styles.quantityNumber}>{`\u00D7`}{quantity}</span>
                <button className={styles.roundButton} type='button' onClick={() => changeQuantity(1)}>
                    <img src={plusIcon}/>
                </button>
                {
                    (maxShowableQuantity !== undefined && quantity > maxShowableQuantity) ?
                        <span className={styles.quantityOverflow}>{messages!.counter_overflow.replace('{n}', (quantity - maxShowableQuantity).toString())}</span>
                        :
                        null
                }
                
            </div>
        )}

    </div>
)}

export function ConfiguratorQuantityCheckbox({ quantity, question, selectedValue, values, maxShowableQuantity,
                                               handleValueChange }: ConfiguratorQuantityProps) {

    const id = question.id;
    const falseValue = values.find(v => v.order === 0);
    const trueValue = values.find(v => v.order === 1);

    if(!falseValue || !trueValue) {
        throw new Error('Invalid checkbox. Needs a false (order 0) and true (order 1) value');
    }

    const onChange = (v:boolean) => {
        handleValueChange(v ? trueValue.values : falseValue.values, v ? 1 : 0);
    }

    return <div className={`${styles.enumYesNoCtrl} ${question.level === 1 ? styles.eyncSub1 : ''}`}>
        <div className={styles.eyncCtrl}>
            <Checkbox 
                    id={id} name={question.id}
                    checked={trueValue.values[0] === selectedValue}
                    onChange={e => onChange(e.target.checked)} 
                    disabled = {trueValue?.disabled ?? false}/>
        
        { question.title ? <label className={`${styles.eyncTitle} ${trueValue.disabled ? styles.disabled : ''}`} htmlFor={id}>
                                {question.title}
                            </label>
                        :
                        null
        }

        {selectedValue === trueValue.values[0] && 
            <QuantityQuestionItem 
                quantity={quantity}
                maxQuantity={trueValue?.maxQuantity ?? DEFAULT_MAX_QUANTITY}
                maxShowableQuantity={maxShowableQuantity}
                value={trueValue} 
                showThumbnail={true}
                onUndoSelect={()=> {}}
                onQuantityChange={(quantity) => handleValueChange(trueValue.values, quantity)}
            />
        }  
        </div> 
              
    </div>

}

export function ConfiguratorQuantityEnumThumb({ quantity, question, selectedValue, values, 
                                                handleValueChange }: ConfiguratorQuantityProps) {
    const { thumbnailsPath, thumbnailWidth, thumbnailHeight } = useEnvConfig();

    const selectedValueDef = values.find((value) => value.values[0] === selectedValue);
    
    return (
        <div className={styles.infoContainer}>
            <ThumbnailMenu imgWidth={thumbnailWidth}>
            { 
                values.map(value => {
                    const onClick = () => {
                        if(value.values[0] != selectedValue){
                            handleValueChange(value.values, 1);
                        }
                    }
                    return <ThumbnailMenuItem type="base" key={`${question.id}-${value.values[0]}`}
                                image={`${thumbnailsPath}/${value.thumbnail ?? ''}`} 
                                imgWidth={thumbnailWidth} imgHeight={thumbnailHeight}
                                selected={value.values[0] === selectedValue}
                                onClick={onClick} disabled={value?.disabled}/>
                })
            }
            </ThumbnailMenu>

            {selectedValueDef ? <>
                    { selectedValueDef.title ? 
                        <div className={styles.infoSubTitleContainer}>
                            <InfoSubtitle text={selectedValueDef.title}/>
                        </div>
                        :
                        null
                    }
                    <div role="presentation" className={styles.enumQuantityWrapper}>
                    <QuantityQuestionItem 
                            quantity={quantity}
                            maxQuantity={selectedValueDef.maxQuantity ?? DEFAULT_MAX_QUANTITY} 
                            value={selectedValueDef} 
                            showThumbnail={false}
                            onUndoSelect={()=> {}}
                            onQuantityChange={(quantity) => handleValueChange(selectedValueDef.values, quantity)}
                            />
                    </div>
                </>
                /*
                <>
                    <div className={styles.infoSubTitleContainer}>
                        <InfoSubtitle text={selectedValueArray.title}/>
                    </div>

                    <div className={styles.numberThumbContainer}>
                    <button className={styles.roundButton} type='button' onClick={() => changeQuantity(-1)}>
                        <img src={minusIcon}/>
                    </button>
                    <span className={styles.quantityNumber}>{quantity}</span>
                    <button className={styles.roundButton} type='button' onClick={() => changeQuantity(1)}>
                        <img src={plusIcon}/>
                    </button>
                    </div>
                </>
                */
               :
               null
            }
        </div>
    )}